import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Copy, Flame, Share2, Receipt, Trash2, RefreshCw,
  Camera, Loader2, ImageIcon, Square, Sparkles, Download, MapPin, StickyNote
} from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import type { SessionDetailViewProps } from "@/ui/view-props";
import { UNIT_VALUE } from "@/lib/score";
import { FullScreenImageViewer } from "@/components/FullScreenImageViewer";
import { PlayerQuickStatsCard } from "@/components/PlayerQuickStatsCard";
import { PlayerAvatar } from "@/components/PlayerAvatar";

export function SessionDetailClassicView(p: SessionDetailViewProps) {
  const navigate = useNavigate();
  const playerRoutePrefix = p.isReadOnly ? `/public/${p.targetUserId}/player` : "/player";

  if (p.loading) {
    return (
      <div className="py-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!p.session) {
    return (
      <div className="py-6 text-center">
        <p className="text-muted-foreground">Session not found.</p>
        <Link to={p.isReadOnly ? `/public/${p.targetUserId}/sessions` : "/sessions"}>
          <Button variant="link">Back to Sessions</Button>
        </Link>
      </div>
    );
  }

  const buyInUnit = p.buyInUnit ?? UNIT_VALUE;
  const totalPot = p.results.reduce((sum, r) => sum + r.buyIns * buyInUnit, 0);
  const totalWon = p.results.reduce((sum, r) => r.net > 0 ? sum + r.net : sum, 0);
  const totalExpenses = p.expenses?.reduce((sum, e) => sum + Number(e.amount), 0) || 0;

  const spentVsGainData = p.results.map(r => ({
    name: r.name.length > 8 ? r.name.slice(0, 8) + "…" : r.name,
    Spent: r.buyIns * buyInUnit,
    Gained: r.cashOut > 0 ? r.cashOut : 0,
  }));

  const scoreSheetSection = (p.scoreImageUrl || !p.isReadOnly) && (
    <div className="space-y-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
        <Camera className="h-3 w-3" />
        Handwritten Score Sheet
      </h3>
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0 relative group">
          {p.scoreImageUrl ? (
            <div className="relative">
              <FullScreenImageViewer
                src={p.scoreImageUrl}
                trigger={
                  <img
                    src={p.scoreImageUrl}
                    alt="Score Sheet"
                    className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  />
                }
              />
              {!p.isReadOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute top-2 right-2 h-8 w-8 shadow-md"
                      disabled={p.isUploading}
                    >
                      {p.isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => document.getElementById('camera-detail')?.click()}>
                      <Camera className="mr-2 h-4 w-4" /><span>Take Photo</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => document.getElementById('gallery-detail')?.click()}>
                      <ImageIcon className="mr-2 h-4 w-4" /><span>Photo Library</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ) : (
            !p.isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="w-full h-32 flex flex-col items-center justify-center border-2 border-dashed border-muted rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    {p.isUploading ? (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Camera className="h-8 w-8 text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground font-medium">Upload Score Sheet</span>
                      </>
                    )}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={() => document.getElementById('camera-detail')?.click()}>
                    <Camera className="mr-2 h-4 w-4" /><span>Take Photo</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => document.getElementById('gallery-detail')?.click()}>
                    <ImageIcon className="mr-2 h-4 w-4" /><span>Photo Library</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          )}
          <input type="file" accept="image/*" capture="environment" id="camera-detail" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(f); }} />
          <input type="file" accept="image/*" id="gallery-detail" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadScoreSheet(f); }} />
        </CardContent>
      </Card>
    </div>
  );

  const handleExport = () => {
    if (!p.onExportXlsx) return;
    
    const svgElement = document.querySelector('.recharts-surface');
    if (svgElement) {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width * 2;
        canvas.height = img.height * 2;
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(2, 2);
          ctx.drawImage(img, 0, 0);
        }
        const pngData = canvas.toDataURL("image/png");
        p.onExportXlsx?.(pngData);
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    } else {
      p.onExportXlsx();
    }
  };

  return (
    <div className="py-6 space-y-4 pb-24">
      {/* Header: back + title + status */}
      <div className="flex items-center gap-2">
        <Link to={p.isReadOnly ? `/public/${p.targetUserId}/sessions` : "/sessions"}>
          <Button size="icon" variant="ghost"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight">Session Results</h1>
          <div className="text-sm text-muted-foreground">
            {p.isEditing ? (
              <input
                type="datetime-local"
                className="bg-background border rounded px-2 py-1 text-xs"
                value={p.editedDate ? p.editedDate.substring(0, 16) : ""}
                onChange={(e) => p.setEditedDate?.(e.target.value)}
              />
            ) : (
              format(new Date(p.session.date), "MMM d, yyyy h:mm a")
            )}
          </div>
          {p.isEditing ? (
            <input
              type="text"
              placeholder="Venue (optional)"
              className="bg-background border rounded px-2 py-1 text-xs mt-1 w-full"
              value={p.editedVenue ?? ''}
              onChange={(e) => p.setEditedVenue?.(e.target.value)}
            />
          ) : p.venue ? (
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{p.venue}</span>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {p.isEditing && (
            <>
              <Button size="sm" onClick={p.onSave}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => p.setIsEditing?.(false)}>Cancel</Button>
            </>
          )}
          <Badge variant={p.session.status === "settled" ? "secondary" : "default"}>
            {p.session.status}
          </Badge>
        </div>
      </div>

      {/* Score Sheet (player results) */}
      <div className="space-y-2">
        {p.results.map((r, i) => (
          <Card key={i} className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className={cn(
                  "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold",
                  r.totalNet > 0 ? "bg-green-100 text-green-700" : r.totalNet < 0 ? "bg-red-100 text-red-600" : "bg-muted text-muted-foreground"
                )}>
                  {i + 1}
                </div>
                <PlayerAvatar 
                  name={r.name} 
                  avatarUrl={r.avatar_url} 
                  size={32}
                  className="shrink-0"
                />
                <div
                  className={cn("min-w-0", !p.isEditing && "cursor-pointer")}
                  onClick={p.isEditing ? undefined : () => navigate(`${playerRoutePrefix}/${r.id}`)}
                >
                  <PlayerQuickStatsCard playerId={r.id} playerName={r.name}>
                    <p className="font-semibold text-sm truncate">{r.name}</p>
                  </PlayerQuickStatsCard>
                  {p.isEditing ? (
                    <div className="flex gap-2 mt-1">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-bold">BUY-INS</span>
                        <input
                          type="number"
                          className="bg-background border rounded w-12 px-1 text-[10px]"
                          value={p.editedResults?.find(er => er.id === r.id)?.buyIns || 0}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const updated = p.editedResults?.map(er => er.id === r.id ? { ...er, buyIns: val } : er) || [];
                            p.setEditedResults?.(updated);
                          }}
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[8px] font-bold">CASH-OUT</span>
                        <input
                          type="number"
                          className="bg-background border rounded w-16 px-1 text-[10px]"
                          value={p.editedResults?.find(er => er.id === r.id)?.cashOut || 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const updated = p.editedResults?.map(er => er.id === r.id ? { ...er, cashOut: val } : er) || [];
                            p.setEditedResults?.(updated);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-[10px] text-muted-foreground uppercase font-bold">
                      <span className="text-blue-500 mr-1">Spent: {formatCurrency(r.buyIns * buyInUnit)}</span>
                      Net: {r.net >= 0 ? "+" : ""}{formatCurrency(r.net)} | Exp: {r.expensePaid - r.expenseShare >= 0 ? "+" : ""}{formatCurrency(r.expensePaid - r.expenseShare)}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className={cn(
                  "text-lg font-bold font-mono",
                  r.totalNet > 0 ? "text-green-600" : r.totalNet < 0 ? "text-red-500" : "text-muted-foreground"
                )}>
                  {r.totalNet >= 0 ? "+" : ""}{formatCurrency(r.totalNet)}
                </span>
                <p className="text-[8px] text-muted-foreground font-bold">TOTAL NET</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Session Notes */}
      {p.isEditing ? (
        <div className="space-y-1">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <StickyNote className="h-3 w-3" />Session Note
          </label>
          <textarea
            placeholder="Add a note (e.g. Pre-Diwali game, Suresh left early)"
            className="bg-background border rounded px-2 py-1 text-xs w-full min-h-[60px] resize-y"
            value={p.editedNotes ?? ''}
            onChange={(e) => p.setEditedNotes?.(e.target.value)}
          />
        </div>
      ) : p.notes ? (
        <Card className="border-0 shadow-sm bg-muted/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1">
              <StickyNote className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Note</span>
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{p.notes}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Spent vs Gain chart */}
      {p.results.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 pb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Spent vs Gained
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={spentVsGainData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9 }} />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }}
                />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Spent" fill="hsl(var(--destructive))" radius={[3, 3, 0, 0]} opacity={0.8} />
                <Bar dataKey="Gained" fill="hsl(142 76% 36%)" radius={[3, 3, 0, 0]} opacity={0.85} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Won</p>
            <p className="text-xl font-bold font-mono text-success">{formatCurrency(totalWon)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Cash Out</p>
            <p className="text-xl font-bold font-mono text-blue-500">{formatCurrency(p.totalCashOut || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Expenses</p>
            <p className="text-xl font-bold font-mono text-orange-500">{formatCurrency(totalExpenses)}</p>
          </CardContent>
        </Card>
      </div>
      <p className="text-[10px] text-muted-foreground uppercase font-bold text-center">
        Total Pot: {formatCurrency(totalPot)} • {p.expenses?.length || 0} receipts
      </p>

      {/* Handwritten score sheet image */}
      {scoreSheetSection}

      {/* Expenses list */}
      {p.expenses && p.expenses.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Receipt className="h-3 w-3" />Expenses
          </h3>
          <div className="space-y-2">
            {p.expenses.map((expense) => (
              <Card key={expense.id} className="border-0 shadow-sm bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{expense.description || 'Expense'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Paid by {expense.payer?.name} • {expense.participants?.length} players
                      </p>
                    </div>
                    <p className="font-bold font-mono text-sm">{formatCurrency(expense.amount)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Roast */}
      {!p.isReadOnly && (!p.roast ? (
        <Button className="w-full h-12 gap-2" variant="secondary" onClick={p.onGenerateRoast} disabled={p.generating}>
          <Flame className="h-5 w-5 text-orange-500" />
          {p.generating ? "Generating..." : "Generate Roast 🔥"}
        </Button>
      ) : (
        <Card className="border-0 shadow-sm bg-accent/10">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">AI Roast</span>
              </div>
              {p.roastSpeaking && (
                <Button size="sm" variant="destructive" className="h-6 px-2 text-[10px] gap-1" onClick={p.onStopRoast}>
                  <Square className="h-3 w-3 fill-current" /> Stop
                </Button>
              )}
            </div>
            <p className="text-sm whitespace-pre-wrap italic">{p.roast}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1 h-8 text-xs" onClick={p.onCopyRoast}>
                <Copy className="h-3 w-3" /> Copy
              </Button>
              <Button size="sm" variant="outline" className="gap-1 h-8 text-xs" onClick={p.onShareWhatsApp}>
                <Share2 className="h-3 w-3" /> WhatsApp
              </Button>
              <Button size="sm" variant="ghost" className="gap-1 h-8 text-xs ml-auto" onClick={p.onGenerateRoast} disabled={p.generating}>
                <RefreshCw className={cn("h-3 w-3", p.generating && "animate-spin")} /> Re-roast
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Quick Actions — at the absolute bottom */}
      {!p.isEditing && (
        <div className="flex gap-2 pt-2 border-t border-border flex-wrap">
          {p.onExportXlsx && (
            <Button size="sm" variant="outline" onClick={handleExport} className="gap-1" title="Export XLSX">
              <Download className="h-3.5 w-3.5" /> XLSX
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={p.onShareWhatsApp} className="gap-1 text-green-600 border-green-200 hover:bg-green-50" title="Share Summary">
            <Share2 className="h-3.5 w-3.5" /> WhatsApp
          </Button>
          {!p.isReadOnly && (
            <>
              {p.onRematch && (
                <Button size="sm" variant="outline" onClick={p.onRematch} className="gap-1 flex-1" data-testid="rematch-button">
                  <RefreshCw className="h-3.5 w-3.5" /> Rematch
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => p.setIsEditing?.(true)} className="flex-1">
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-1"
                onClick={p.onDelete}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
